import React, { useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Link } from "react-router-dom";
import MainLayOut from "../../layout/MainLayOut";
import { useAuth } from "./AuthContext";
import { useGlobal } from "../../global/GlobalContext";

const Spinner = () => (
  <svg className="animate-spin h-5 w-5 text-white inline-block" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
);

const Register = () => {
  const [name, setName]           = useState("");
  const [gender, setGender]       = useState("");
  const [dob, setDob]             = useState("");
  const [username, setUsername]   = useState("");
  const [mobile_no, setMobileNo]  = useState("");
  const [password, setPassword]   = useState("");
  const [errors, setErrors]       = useState({});
  const [loading, setLoading]     = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showPassword, setShowPassword]   = useState(false);
  const [verifyPending, setVerifyPending] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState("");

  const { setToken } = useAuth();
  const { mergeLocalCartWithServer } = useGlobal();

  const validateForm = () => {
    const errs = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;

    if (!name.trim()) errs.name = "Name is required";
    else if (name.length < 3) errs.name = "Name must be at least 3 characters";

    if (!username.trim()) errs.username = "Email is required";
    else if (!emailRegex.test(username)) errs.username = "Enter a valid email address";

    if (!mobile_no.trim()) errs.mobile_no = "Mobile number is required";
    else if (!phoneRegex.test(mobile_no)) errs.mobile_no = "Enter a valid 10-digit mobile number";

    if (!password.trim()) errs.password = "Password is required";
    else if (password.length < 6) errs.password = "Password must be at least 6 characters";

    if (!dob || new Date(dob) > new Date(new Date().setFullYear(new Date().getFullYear() - 10)))
      errs.dob = "You must be at least 10 years old";

    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const registerUser = async () => {
    if (!validateForm()) return;
    setLoading(true);
    setErrors({});
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v1/webuser/sign_up`,
        { username, password, name, gender, dob, mobile_no },
        { headers: { "Content-Type": "application/json" } }
      );
      const data = response.data;
      if (!data.status) {
        setErrors({ form: data.message || "Registration failed." });
        return;
      }
      setToken(data.user_token);
      localStorage.setItem("username", data.user_data.name);
      localStorage.setItem("useremail", data.user_data.email);
      localStorage.setItem("uservarified", data.user_data.email_verified_at);
      await mergeLocalCartWithServer(data.user_token);

      if (data.user_data.email && data.user_data.email_verified_at === null) {
        setRegisteredEmail(data.user_data.email);
        setVerifyPending(true);
      }
    } catch (error) {
      setErrors({ form: error.response?.data?.message || "Something went wrong. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const resendHandler = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v1/resend_email_verification`,
        { headers: { Authorization: `Bearer ${localStorage.getItem("userToken")}` } }
      );
      if (response.data.status) {
        setResendSuccess(true);
        setTimeout(() => setResendSuccess(false), 5000);
      } else {
        toast.error(response.data.message || "Failed to resend", { position: "top-center" });
      }
    } catch {
      toast.error("Failed to resend. Please try again.", { position: "top-center" });
    } finally {
      setResendLoading(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-3.5 py-2.5 border rounded-lg text-sm outline-none transition-colors ${
      errors[field]
        ? "border-red-400 bg-red-50 focus:border-red-500"
        : "border-gray-300 focus:border-brand-600"
    }`;

  if (verifyPending) {
    return (
      <MainLayOut>
        <ToastContainer />
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 px-4">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
            {/* Icon */}
            <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <svg className="w-10 h-10 text-brand-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25H4.5a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
            </div>

            <h2 className="text-xl font-bold text-gray-900 mb-2">Check your inbox</h2>
            <p className="text-gray-500 text-sm mb-1">We sent a verification link to</p>
            <p className="font-semibold text-brand-600 text-sm mb-6 break-all">{registeredEmail}</p>

            <div className="bg-blue-50 border border-blue-100 rounded-xl px-4 py-3.5 text-sm text-blue-700 text-left mb-6">
              Click the link in the email to verify your account. Check your spam folder if you don't see it.
            </div>

            {resendSuccess ? (
              <div className="flex items-center justify-center gap-2 text-green-600 text-sm font-medium bg-green-50 border border-green-200 rounded-xl px-4 py-3 mb-4">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Verification email resent successfully!
              </div>
            ) : (
              <p className="text-gray-400 text-sm mb-4">
                Didn't receive it?{" "}
                <button
                  onClick={resendHandler}
                  disabled={resendLoading}
                  className="text-brand-600 font-semibold hover:underline disabled:opacity-60 inline-flex items-center gap-1.5"
                >
                  {resendLoading ? <><Spinner /> <span className="text-sm">Sending...</span></> : "Resend email"}
                </button>
              </p>
            )}

            <Link to="/login" className="text-sm text-gray-400 hover:text-gray-600 underline">
              Back to login
            </Link>
          </div>
        </div>
      </MainLayOut>
    );
  }

  return (
    <MainLayOut>
      <ToastContainer />
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-blue-50 px-4 py-10">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-7">
            <h2 className="text-2xl font-bold text-gray-900">Create an account</h2>
            <p className="text-gray-500 text-sm mt-1">Register with MPI Plastic</p>
          </div>

          {errors.form && (
            <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 text-sm px-4 py-3 rounded-xl mb-5">
              <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.form}
            </div>
          )}

          <form onSubmit={(e) => { e.preventDefault(); registerUser(); }} className="space-y-4">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name <span className="text-red-500">*</span></label>
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                className={inputClass("name")} placeholder="Enter your full name" />
              {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
            </div>

            {/* Gender + DOB row */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select value={gender} onChange={(e) => setGender(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-lg text-sm outline-none focus:border-brand-600 text-gray-700 bg-white">
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth <span className="text-red-500">*</span></label>
                <input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
                  className={inputClass("dob")} max={new Date().toISOString().split("T")[0]} />
                {errors.dob && <p className="text-red-500 text-xs mt-1">{errors.dob}</p>}
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-500">*</span></label>
              <input type="email" value={username} onChange={(e) => setUsername(e.target.value)}
                className={inputClass("username")} placeholder="you@example.com" />
              {errors.username && <p className="text-red-500 text-xs mt-1">{errors.username}</p>}
            </div>

            {/* Mobile */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number <span className="text-red-500">*</span></label>
              <input type="tel" value={mobile_no}
                onChange={(e) => setMobileNo(e.target.value.replace(/\D/g, "").slice(0, 10))}
                className={inputClass("mobile_no")} placeholder="10-digit mobile number" maxLength={10} />
              {errors.mobile_no && <p className="text-red-500 text-xs mt-1">{errors.mobile_no}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password <span className="text-red-500">*</span></label>
              <div className="relative">
                <input type={showPassword ? "text" : "password"} value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={inputClass("password") + " pr-10"} placeholder="Min. 6 characters" />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showPassword ? <FaEye size={17} /> : <FaEyeSlash size={17} />}
                </button>
              </div>
              {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
            </div>

            {/* Submit */}
            <button type="submit" disabled={loading}
              className="w-full py-2.5 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-lg text-sm transition-colors disabled:opacity-70 flex items-center justify-center gap-2 mt-1">
              {loading ? (
                <><Spinner /> <span>Creating account...</span></>
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-5">
            Already have an account?{" "}
            <Link to="../login" className="text-brand-600 font-semibold hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </MainLayOut>
  );
};

export default Register;
